/**
 * Utilities for working with WMS (Web Map Service) tile requests
 */

import { publicLayersState, alkisToggleState } from '@projects/pages/detail/AlkisMap/layersState';
import { useSnapshot } from 'valtio';

/**
 * Convert tile coordinates to EPSG:3857 (Web Mercator) bounding box
 * This matches the standard Web Mercator projection used by Mapbox and other tile services
 * Based on: https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames
 */
export const tileToEpsg3857BBox = (tileX: number, tileY: number, zoom: number): string => {
  const tileSize = 256; // Standard tile size
  const earthRadius = 6378137; // Earth radius in meters (WGS84)
  const earthCircumference = 2 * Math.PI * earthRadius;

  // Calculate the resolution at this zoom level (meters per pixel)
  const resolution = earthCircumference / (tileSize * Math.pow(2, zoom));

  // Web Mercator origin is at (-20037508.342789, -20037508.342789)
  const originShift = earthCircumference / 2;

  // Calculate tile bounds in Web Mercator coordinates
  const xMin = tileX * tileSize * resolution - originShift;
  const xMax = (tileX + 1) * tileSize * resolution - originShift;
  const yMax = originShift - tileY * tileSize * resolution;
  const yMin = originShift - (tileY + 1) * tileSize * resolution;

  return `${xMin},${yMin},${xMax},${yMax}`;
};

/**
 * Standard WMS tile dimension for crisp raster rendering.
 * 512x512 ensures crisp, high-DPI rendering for vector lines and parcel typography.
 */
export const WMS_TILE_SIZE = 512;

/** Default ALKIS request size: 2x oversampling of the base tile size. */
export const WMS_HIDPI_TILE_SIZE = 1024;

/** Hard cap for a single WMS GetMap request (bandwidth / GPU memory). */
export const WMS_MAX_TILE_SIZE = 2048;

/** DPI hint sent to WMS servers that scale line styling by resolution. */
export const WMS_HIDPI_DPI = 180;

/** SSR-safe device pixel ratio. */
export const getDevicePixelRatio = (): number => {
  if (typeof window !== 'undefined' && window.devicePixelRatio) {
    return window.devicePixelRatio;
  }
  return 1;
};

/**
 * Resolves the WMS WIDTH/HEIGHT for an ALKIS tile.
 * Prefers the backend tileSize hint when larger, scales with devicePixelRatio.
 */
/** At this zoom and closer, parcel edges fill the screen: request maximum density. */
export const ALKIS_CLOSEUP_ZOOM = 19;

export const resolveAlkisTileSize = (layerTileSize?: number, zoom?: number): number => {
  if (zoom !== undefined && zoom >= ALKIS_CLOSEUP_ZOOM) {
    return WMS_MAX_TILE_SIZE;
  }
  const dpr = getDevicePixelRatio();
  const hidpi = dpr >= 2 ? WMS_MAX_TILE_SIZE : WMS_HIDPI_TILE_SIZE;
  const hinted = layerTileSize && Number.isFinite(layerTileSize) ? layerTileSize : 0;
  return Math.max(WMS_TILE_SIZE, Math.min(WMS_MAX_TILE_SIZE, Math.max(hidpi, hinted)));
};

/**
 * Hook to fetch WMS URL for the selected layer from valtio state
 * Uses the stored selectedUrl and selectedLayer to build the WMS request
 */
/**
 * Appends HiDPI vendor hints to a templated URL string.
 * Unknown params are ignored by WMS servers, so this is safe for all vendors.
 */
const withHiDpiVendorParams = (url: string, dpi: number): string => {
  let result = url;
  if (!/[?&]DPI=/i.test(result)) result += `&DPI=${dpi}`;
  if (!/[?&]MAP_RESOLUTION=/i.test(result)) result += `&MAP_RESOLUTION=${dpi}`;
  if (!/[?&]FORMAT_OPTIONS=/i.test(result)) result += `&FORMAT_OPTIONS=dpi:${dpi}`;
  return result;
};

export const buildWmsUrl = (
  rawUrl: string,
  layerName: string,
  tileX: number,
  tileY: number,
  zoom: number,
  tileSize: number = WMS_TILE_SIZE,
  dpi: number = WMS_HIDPI_DPI,
): string => {
  const bbox = tileToEpsg3857BBox(tileX, tileY, zoom);

  // If the backend provided a templated URL (standard in mapd / conmon-fe)
  if (rawUrl.includes('{bbox}') || rawUrl.includes('{bbox-epsg-3857}')) {
    const replaced = rawUrl
      .replace('{layers}', layerName)
      .replace('{bbox-epsg-3857}', bbox)
      .replace('{bbox}', bbox)
      .replace('CRS=&', 'CRS=EPSG:3857&')
      .replace(/WIDTH=\d+/i, `WIDTH=${tileSize}`)
      .replace(/HEIGHT=\d+/i, `HEIGHT=${tileSize}`);
    return withHiDpiVendorParams(replaced, dpi);
  }

  // Otherwise, safely parse and update query parameters using URL
  try {
    const url = new URL(rawUrl);
    const version = url.searchParams.get('VERSION') || '1.3.0';

    url.searchParams.set('SERVICE', 'WMS');
    url.searchParams.set('REQUEST', 'GetMap');
    url.searchParams.set('VERSION', version);
    if (version === '1.1.1') {
      url.searchParams.set('SRS', 'EPSG:3857');
      url.searchParams.delete('CRS');
    } else {
      url.searchParams.set('CRS', 'EPSG:3857');
      url.searchParams.delete('SRS');
    }
    url.searchParams.set('FORMAT', 'image/png');
    url.searchParams.set('TRANSPARENT', 'TRUE');
    url.searchParams.set('WIDTH', String(tileSize));
    url.searchParams.set('HEIGHT', String(tileSize));
    url.searchParams.set('BBOX', bbox);
    url.searchParams.set('LAYERS', layerName);
    if (!url.searchParams.has('STYLES')) {
      url.searchParams.set('STYLES', '');
    }
    // HiDPI hints for servers that scale styling by resolution; ignored otherwise.
    if (!url.searchParams.has('DPI')) {
      url.searchParams.set('DPI', String(dpi));
    }
    if (!url.searchParams.has('MAP_RESOLUTION')) {
      url.searchParams.set('MAP_RESOLUTION', String(dpi));
    }
    if (!url.searchParams.has('FORMAT_OPTIONS')) {
      url.searchParams.set('FORMAT_OPTIONS', `dpi:${dpi}`);
    }

    return url.toString();
  } catch {
    // Fallback if URL parsing fails
    const params = new URLSearchParams({
      SERVICE: 'WMS',
      REQUEST: 'GetMap',
      VERSION: '1.3.0',
      CRS: 'EPSG:3857',
      FORMAT: 'image/png',
      TRANSPARENT: 'TRUE',
      WIDTH: String(tileSize),
      HEIGHT: String(tileSize),
      BBOX: bbox,
      LAYERS: layerName,
      STYLES: '',
    });
    const base = rawUrl.split('?')[0];
    return `${base}?${params.toString()}`;
  }
};

/**
 * WFS GetFeature URL builder for an explicit WFS base URL.
 *
 * A WFS URL can NOT be derived from the WMS URL: state endpoints expose WFS
 * on different paths (e.g. Hamburg WMS `HH_WMS_Geobasisdaten` vs WFS
 * `WFS_HH_ALKIS_vereinfacht`). The base URL must come from backend layer data
 * (`wfsUrl` on the layer, once mapd exposes its paired WFS MapService).
 * Until then these helpers are opt-in only; WMS raster stays the default.
 *
 * Endpoint reality (Apr 2026 GetCapabilities audit): most states serve public
 * ALKIS WFS (Hamburg, Berlin, NRW, Hessen, Niedersachsen verified), often as
 * GML with per-state typename/axis-order quirks; Bayern has no public WFS.
 */
export const WFS_OUTPUT_FORMAT = 'application/geojson';
export const WFS_MAX_FEATURES = 1000;

export type WfsUrlOptions = {
  outputFormat?: string;
  count?: number;
};

/** True when a layer carries a usable explicit WFS source. */
export const hasWfsSource = (wfsUrl?: string, wfsLayerName?: string): boolean =>
  Boolean(wfsUrl && wfsUrl.trim() && wfsLayerName && wfsLayerName.trim());

export const buildWfsUrl = (
  wfsBaseUrl: string,
  typeName: string,
  bbox: string,
  options: WfsUrlOptions = {},
): string | null => {
  const base = wfsBaseUrl.split('?')[0]?.trim();
  if (!base || !typeName) return null;

  const { outputFormat = WFS_OUTPUT_FORMAT, count = WFS_MAX_FEATURES } = options;

  // Preserve auth-style params already on the configured base URL.
  const preserved = new URLSearchParams();
  try {
    const parsed = new URL(wfsBaseUrl);
    parsed.searchParams.forEach((value, key) => {
      preserved.set(key, value);
    });
  } catch {
    // Relative base URL: no params to preserve.
  }

  const params = new URLSearchParams({
    SERVICE: 'WFS',
    REQUEST: 'GetFeature',
    VERSION: '2.0.0',
    TYPENAMES: typeName,
    SRSNAME: 'EPSG:3857',
    BBOX: `${bbox},EPSG:3857`,
    outputFormat,
    count: String(count),
  });
  preserved.forEach((value, key) => {
    params.set(key, value);
  });

  return `${base}?${params.toString()}`;
};

export const buildWfsUrlFromTile = (
  wfsBaseUrl: string,
  typeName: string,
  tileX: number,
  tileY: number,
  zoom: number,
  options: WfsUrlOptions = {},
): string | null =>
  buildWfsUrl(wfsBaseUrl, typeName, tileToEpsg3857BBox(tileX, tileY, zoom), options);

export const useAlkisWmsUrl = (tileX: number, tileY: number, zoom: number): string | null => {
  const { selectedLayer, selectedUrl } = useSnapshot(publicLayersState);
  const { enabled: alkisEnabled } = useSnapshot(alkisToggleState);

  // Return null if ALKIS is disabled
  if (!alkisEnabled) {
    return null;
  }

  // Return null if no layer or URL is selected
  if (!selectedLayer || !selectedUrl) {
    return null;
  }

  const layerName = (selectedLayer.layerName as string) || (selectedLayer.name as string) || '';
  if (!layerName) {
    return null;
  }

  return buildWmsUrl(
    selectedUrl as string,
    layerName,
    tileX,
    tileY,
    zoom,
    resolveAlkisTileSize(selectedLayer.tileSize, zoom),
  );
};
